import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, UserCog, GraduationCap, TrendingUp, Calendar, AlertCircle,
  Building2, Activity as ActivityIcon, CheckCircle2, Clock, MapPin,
} from 'lucide-react';
import {
  PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis,
  Tooltip, CartesianGrid,
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TEAL = '#208178';
const COLORS_DONUT = ['#208178', '#cbd5e1'];

export default function EseDashboard() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ese/dashboard`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(res.data);
    } catch (e) {
      toast.error('No se pudo cargar el panel');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="text-center text-slate-400 py-20" data-testid="ese-dashboard-loading">
        Cargando panel...
      </div>
    );
  }

  if (!data) return null;

  const { trabajadores, usuarios_portal, areas, actividades, matriz_resumen, top_pendientes, proximas_sesiones } = data;
  const donutData = [
    { name: 'Activos', value: trabajadores.activos },
    { name: 'Inactivos', value: trabajadores.inactivos },
  ];

  return (
    <div className="space-y-6" data-testid="ese-dashboard">
      {/* Header */}
      <div className="rounded-xl px-5 py-4 text-white" style={{ background: 'linear-gradient(135deg, #208178 0%, #156055 100%)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold">Panel Central</h1>
            <p className="text-sm text-white/85">Resumen ejecutivo de tu gestión de trabajadores.</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold">{matriz_resumen.cobertura_pct}%</p>
            <p className="text-xs text-white/70 uppercase tracking-wide">Cobertura capacitaciones</p>
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={Users} label="Trabajadores" value={trabajadores.total} sub={`${trabajadores.activos} activos`} color="#3B82F6" testId="kpi-trabajadores" onClick={() => navigate('/ese/trabajadores')} />
        <KpiCard icon={UserCog} label="Usuarios portal" value={usuarios_portal} sub="Cuentas administrativas" color="#8B5CF6" testId="kpi-usuarios" onClick={() => navigate('/ese/usuarios')} />
        <KpiCard icon={GraduationCap} label="Asignaciones" value={matriz_resumen.total_asignaciones} sub={`${matriz_resumen.aprobadas} aprobadas`} color="#1B919B" testId="kpi-asignaciones" onClick={() => navigate('/ese/matriz')} />
        <KpiCard icon={AlertCircle} label="Pendientes" value={matriz_resumen.pendientes} sub="Por aprobar" color="#F59E0B" testId="kpi-pendientes" onClick={() => navigate('/ese/matriz')} />
      </div>

      {/* Fila 2: Donut estado + Cobertura */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Donut activos vs inactivos */}
        <Panel title="Estado de Trabajadores" icon={Users}>
          {trabajadores.total === 0 ? (
            <EmptyState text="Aun no hay trabajadores" />
          ) : (
            <div className="flex items-center gap-4">
              <div className="w-36 h-36 shrink-0 relative">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={donutData} dataKey="value" innerRadius={42} outerRadius={64} paddingAngle={2}>
                      {donutData.map((_, i) => <Cell key={i} fill={COLORS_DONUT[i]} />)}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <p className="text-2xl font-bold text-slate-900">{trabajadores.total}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wide">Total</p>
                </div>
              </div>
              <div className="flex-1 space-y-2">
                <LegendRow color={COLORS_DONUT[0]} label="Activos" value={trabajadores.activos} />
                <LegendRow color={COLORS_DONUT[1]} label="Inactivos" value={trabajadores.inactivos} />
              </div>
            </div>
          )}
        </Panel>

        {/* Cobertura */}
        <Panel title="Cobertura de Capacitaciones" icon={TrendingUp} className="lg:col-span-2">
          {matriz_resumen.total_asignaciones === 0 ? (
            <EmptyState text="Aun no hay asignaciones" />
          ) : (
            <div className="space-y-3">
              <div className="flex items-end justify-between">
                <div>
                  <p className="text-4xl font-bold" style={{ color: TEAL }}>{matriz_resumen.cobertura_pct}%</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {matriz_resumen.aprobadas} de {matriz_resumen.total_asignaciones} capacitaciones aprobadas
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-500">Pendientes</p>
                  <p className="text-2xl font-bold text-amber-600">{matriz_resumen.pendientes}</p>
                </div>
              </div>
              <div className="w-full h-3 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all" style={{ width: `${matriz_resumen.cobertura_pct}%`, background: `linear-gradient(90deg, ${TEAL}, #1B919B)` }} />
              </div>
              <div className="grid grid-cols-3 gap-2 text-center text-[10px] uppercase tracking-wide pt-2">
                <div className="rounded-lg bg-emerald-50 border border-emerald-100 py-2">
                  <p className="text-base font-bold text-emerald-700">{matriz_resumen.aprobadas}</p>
                  <p className="text-emerald-700/70">Aprobadas</p>
                </div>
                <div className="rounded-lg bg-amber-50 border border-amber-100 py-2">
                  <p className="text-base font-bold text-amber-700">{matriz_resumen.pendientes}</p>
                  <p className="text-amber-700/70">Pendientes</p>
                </div>
                <div className="rounded-lg bg-slate-50 border border-slate-200 py-2">
                  <p className="text-base font-bold text-slate-700">{matriz_resumen.total_asignaciones}</p>
                  <p className="text-slate-500">Total</p>
                </div>
              </div>
            </div>
          )}
        </Panel>
      </div>

      {/* Fila 3: Distribucion areas y actividades */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Distribucion por Area" icon={Building2} subtitle="Top 10">
          {areas.length === 0 ? <EmptyState text="Aun no hay areas asignadas" /> : (
            <BarChartHorizontal data={areas} color="#3B82F6" />
          )}
        </Panel>
        <Panel title="Distribucion por Actividad" icon={ActivityIcon} subtitle="Top 10">
          {actividades.length === 0 ? <EmptyState text="Aun no hay actividades asignadas" /> : (
            <BarChartHorizontal data={actividades} color="#8B5CF6" />
          )}
        </Panel>
      </div>

      {/* Fila 4: Top pendientes y proximas sesiones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title="Trabajadores con mas Pendientes" icon={AlertCircle} subtitle="Top 5">
          {top_pendientes.length === 0 ? <EmptyState text="Sin pendientes! Todo al dia." /> : (
            <ul className="space-y-2" data-testid="top-pendientes-list">
              {top_pendientes.map((t, i) => (
                <li key={t.id || i}
                  onClick={() => navigate('/ese/matriz')}
                  className="flex items-center gap-3 p-2.5 rounded-lg border border-slate-200 hover:border-amber-300 hover:bg-amber-50/50 cursor-pointer transition-colors">
                  <div className="w-8 h-8 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs font-bold shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-slate-900 truncate">{t.nombre_completo}</p>
                    <p className="text-[10px] text-slate-500 truncate">RUT {t.rut} - {t.porcentaje}% completado</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-amber-600">{t.pendientes}</p>
                    <p className="text-[9px] text-slate-400 uppercase tracking-wide">de {t.total}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel title="Proximas Sesiones" icon={Calendar} subtitle="Siguientes 5">
          {proximas_sesiones.length === 0 ? <EmptyState text="Sin sesiones programadas" /> : (
            <ul className="space-y-2" data-testid="proximas-sesiones-list">
              {proximas_sesiones.map((s) => (
                <li key={s.id}
                  onClick={() => navigate('/ese/sesiones')}
                  className="p-2.5 rounded-lg border border-slate-200 hover:border-teal-300 hover:bg-teal-50/40 cursor-pointer transition-colors">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-teal-700 bg-teal-50 px-2 py-0.5 rounded">
                      {fmtFecha(s.fecha)}
                    </span>
                    {s.hora_inicio && (
                      <span className="text-[10px] text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{s.hora_inicio}
                      </span>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-slate-900 truncate">{s.capacitacion_nombre}</p>
                  <div className="flex items-center justify-between mt-1">
                    {s.lugar ? (
                      <span className="text-[10px] text-slate-500 flex items-center gap-1 truncate">
                        <MapPin className="w-3 h-3 shrink-0" />{s.lugar}
                      </span>
                    ) : <span />}
                    <span className="text-[10px] font-semibold text-slate-600 bg-slate-100 px-2 py-0.5 rounded shrink-0">
                      Mi empresa: {s.inscritos_empresa}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>
    </div>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, testId, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="group bg-white border border-slate-200 rounded-xl p-4 text-left hover:shadow-md hover:border-slate-300 transition-all"
      style={{ borderLeftColor: color, borderLeftWidth: '4px' }}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: `${color}15`, border: `2px solid ${color}` }}>
          <Icon className="w-4 h-4" style={{ color }} strokeWidth={2.2} />
        </div>
      </div>
      <p className="text-2xl font-bold text-slate-900">{value}</p>
      <p className="text-[10px] uppercase tracking-wider text-slate-500 mt-1">{label}</p>
      {sub && <p className="text-[10px] text-slate-400 mt-0.5 truncate">{sub}</p>}
    </button>
  );
}

function Panel({ title, subtitle, icon: Icon, children, className = '' }) {
  return (
    <section className={`bg-white border border-slate-200 rounded-xl p-4 shadow-sm ${className}`}>
      <header className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-slate-400" />}
          <h2 className="text-xs font-bold uppercase tracking-wider text-slate-700">{title}</h2>
        </div>
        {subtitle && <span className="text-[10px] text-slate-400">{subtitle}</span>}
      </header>
      {children}
    </section>
  );
}

function LegendRow({ color, label, value }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span className="flex items-center gap-2 text-slate-600">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
        {label}
      </span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}

function BarChartHorizontal({ data, color }) {
  const chartData = data.map(d => ({ name: d.nombre, value: d.count }));
  return (
    <div style={{ width: '100%', height: Math.max(180, chartData.length * 32) }}>
      <ResponsiveContainer>
        <BarChart data={chartData} layout="vertical" margin={{ top: 4, right: 16, left: 4, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
          <XAxis type="number" tick={{ fontSize: 10, fill: '#64748b' }} allowDecimals={false} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fill: '#475569' }} width={120} />
          <Tooltip cursor={{ fill: '#f8fafc' }} contentStyle={{ fontSize: 11, borderRadius: 8 }} />
          <Bar dataKey="value" fill={color} radius={[0, 6, 6, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center py-8 text-xs text-slate-400">
      <CheckCircle2 className="w-6 h-6 mx-auto mb-2 text-slate-300" />
      {text}
    </div>
  );
}

function fmtFecha(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  const meses = ['ENE','FEB','MAR','ABR','MAY','JUN','JUL','AGO','SEP','OCT','NOV','DIC'];
  return `${d} ${meses[parseInt(m) - 1]} ${y}`;
}
